import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import {
  createNewDataset,
  createNewDatasetV2,
  fetchDatasetPreviews,
  fetchFolderMetadata,
  selectDatasetFolders,
  startFolderUploadTransaction,
  startMultipleFolderUploadTransaction,
} from "redux/CreateDataset/actions";
import useFolderValidation from "./useFolderValidation";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import {
  DATASET_CREATION_V2,
  DATASET_INGESTION_ON_PREM,
} from "helpers/constants/filters";
import { Form } from "antd";

export default function useCreateDataset(batchId?: string) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { newDatasetId } = useParams();
  const { validateFolders, validateFoldersPaths } =
    useFolderValidation(batchId);
  const isNewCreationEnabled =
    useFeatureFlagService(DATASET_CREATION_V2).enabled;
  const isDatasetCreationV3Enabled = useFeatureFlagService(
    DATASET_INGESTION_ON_PREM,
  ).enabled;

  const createDatasetHandler = async (name?: string) => {
    if (isNewCreationEnabled || isDatasetCreationV3Enabled) {
      // Use new workflow action creator
      return await dispatch(createNewDatasetV2() as any);
    } else {
      // Use old dataset action creator
      return await dispatch(createNewDataset(name || "") as any);
    }
  };

  const startFoldersPathsHandler = async (paths: string[]) => {
    await validateFoldersPaths(paths);
    dispatch(fetchFolderMetadata(newDatasetId!) as any);
    form.resetFields();
  };

  const startFolderUpload = async (files: File[]) => {
    try {
      // Validate folders using the validation hook
      const validationResults = await validateFolders(files);

      if (!validationResults) {
        return; // Validation failed
      }

      const { validFolders } = validationResults;

      // Only proceed with valid folders
      if (validFolders.length === 0) {
        return;
      }

      if (validFolders.length === 1) {
        // Single folder scenario (browser selection OR single drag&drop folder)
        const folderGroup = validFolders[0];
        await dispatch(
          startFolderUploadTransaction(
            newDatasetId!,
            folderGroup.folderName,
            folderGroup.files,
            batchId,
          ) as any,
        );
      } else {
        // Multiple folders scenario (only possible via drag&drop)
        await dispatch(
          startMultipleFolderUploadTransaction(
            newDatasetId!,
            validFolders,
            batchId,
          ) as any,
        );
      }
    } catch (error: any) {
      let errorMessage: string =
        error.response?.data?.message ||
        "There was an error starting the folder upload transaction";
      console.error(errorMessage);
    }
  };

  const selectFolders = async (folderIds: string[], batchId?: string) => {
    if (!newDatasetId) {
      throw new Error("No dataset ID available");
    }

    return await dispatch(
      selectDatasetFolders(newDatasetId, folderIds, batchId) as any,
    );
  };

  const fetchPreviews = async () => {
    return await dispatch(fetchDatasetPreviews(newDatasetId!, batchId) as any);
  };

  return {
    createNewDataset: createDatasetHandler,
    startFolderUpload,
    selectFolders,
    fetchPreviews,
    startFoldersPathsHandler,
    form,
  };
}
